import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RappiderButtonModule, RappiderCardWithIconTitleDescriptionListComponent } from '@rappider/rappider-components';
import { ButtonType, MultipleFileUploadConfig } from '@rappider/rappider-components/utils';
import { RappiderMultipleFileUploadComponent } from '@rappider/rappider-components';
import { AiChatConfig } from '../../utils/ai-response-types.interface';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { environment } from '@environment';

@Component({
  selector: 'rappider-ai-start',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RappiderButtonModule,
    RappiderCardWithIconTitleDescriptionListComponent,
    RappiderMultipleFileUploadComponent,
    NzInputModule,
    NzButtonModule
],
  templateUrl: './ai-start.component.html',
  styleUrls: ['./ai-start.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RappiderAiStartComponent implements OnInit, OnChanges {
  // Inputs
  @Input() config?: AiChatConfig;
  @Input() isLoading = false;
  @Input() editingModelName?: string;
  @Input() showExamplePrompts = false;
  @Input() examplePrompts?: string[];

  // Outputs
  @Output() startClicked = new EventEmitter<{ message: string; files?: File[]; configType?: string }>();
  @Output() messageChanged = new EventEmitter<string>();

  // Component properties
  currentMessage = '';
  uploadedFiles: File[] | null = null;

  ButtonType = ButtonType;

  // Button visibility control
  visiblePrompts: string[] = [];
  moreButtonVisible = false;
  showScrollAnimation = false;

  /**
   * Timestamp of the last Enter-triggered submit to avoid accidental double fires
   */
  private lastEnterAt = 0;
  private readonly enterDebounceMs = 300;

  // Example prompts - only use custom prompts if provided
  get allExamplePrompts(): string[] {
    return this.examplePrompts || [];
  }

  // File upload config
  fileUploadConfig: MultipleFileUploadConfig = {
    acceptedFileTypes: '.csv, .txt, .json, .png, .jpg, .jpeg, .pdf, .doc, .docx, .xls, .xlsx, .md, .xml',
    uploadText: 'Upload',
    maxFileCount: environment.maxFileCount ?? 5,
    maxFileSize: environment.maxFileSize ?? 10240,
    showFileList: true,
    listType: 'picture',
    maxHeight: '200px',
    scrollable: true,
  };

  get startTitle(): string {
    return this.config?.title || 'AI Assistant';
  }

  get startDescription(): string {
    return this.config?.description ||
      'How can I help you today? Describe what you need and I\'ll assist you with creating, analyzing, or improving your project.';
  }

  get inputPlaceholder(): string {
    return this.config?.placeholder || 'Describe what you need help with...';
  }

  get buttonText(): string {
    return this.config?.buttonText || 'Start';
  }

  get enableFileUpload(): boolean {
    return this.config?.imageUpload || false;
  }

  get responseTypeName(): string {
    return 'Response';
  }

  get saveButtonText(): string {
    return this.config?.saveButtonText || 'Save';
  }

  get saveButtonIcon(): string {
    return this.config?.saveButtonIcon || 'fa-solid fa-save';
  }

  get configType(): string | undefined {
    return this.config?.type;
  }

  /**
   * Handle textarea keydown events
   * @param event Keyboard event
   */
  onTextareaKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      const now = Date.now();
      if (now - this.lastEnterAt > this.enterDebounceMs) {
        this.lastEnterAt = now;
        this.onStartClick();
      }
    }
  }

  /**
   * Handle start button click
   */
  onStartClick(): void {
    if ((this.currentMessage.trim() || (this.uploadedFiles && this.uploadedFiles.length > 0)) && !this.isLoading) {
      this.startClicked.emit({
        message: this.currentMessage.trim(),
        files: this.uploadedFiles || undefined,
        configType: this.configType
      });
    }
  }
  /**
   * Handle message input changes
   */
  onMessageChange(): void {
    this.messageChanged.emit(this.currentMessage);
  }

  /**
   * Insert example prompt into textarea
   */
  insertExamplePrompt(prompt: string): void {
    this.currentMessage = prompt;
    this.onMessageChange();
  }

  /**
   * Handle card click event
   */
  onCardClick(event: any): void {
    const prompt = event.description || event.key || event.id;
    if (prompt) {
      this.insertExamplePrompt(prompt);
      // Focus on textarea
      setTimeout(() => {
        const textarea = document.getElementById('aiChatInput');
        textarea?.focus();
      }, 100);
    }
  }

  /**
   * Calculate visible buttons based on container width
   */
  adjustButtons(): void {
    this.visiblePrompts = this.allExamplePrompts;
    this.moreButtonVisible = false;
  }

  ngOnInit(): void {
    this.adjustButtons();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['examplePrompts']) {
      this.adjustButtons();
    }
  }

  /**
   * Get card items array for the list component
   */
  getCardItems(): any[] {
    return this.visiblePrompts.map(prompt => ({
      id: prompt,
      title: ' ',
      name: ' ',
      key: ' ',
      description: prompt,
      iconName: 'fa-solid fa-sparkles'
    }));
  }
}
